import { IconDots } from '@tabler/icons-react';
import { getEmployeeEmploymentStatus } from '@zorro/common-utils';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Menu } from '@zorro/design-system/v2/Menu';
import { Pill } from '@zorro/design-system/v2/Pill';
import { Box, Group, Stack, Text, brand } from '@zorro/web/design-system';
import { useMonolithQuery, useRoles } from '@zorro/web/ui-utils';
import { useState } from 'react';

import { EmployeeLayoutAction } from '../EmployeeLayout/EmployeeLayoutActions';
import { getEligibilityStatusBadgeConfig } from './EligibilityStatusBadgeUtils';

type Props = {
  employeeId: string;
  setAction: (action: EmployeeLayoutAction) => void;
};

export const EligibilityStatusBadge = ({ employeeId, setAction }: Props) => {
  const { isZorroOperations } = useRoles();
  const [open, setOpen] = useState(false);

  const { data: employee, isLoading } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const { data: hasPreviousEligibility } = useMonolithQuery({
    method: 'employeesControllerHasPreviousEligibility',
    params: [employeeId],
  });

  if (!employee || isLoading) {
    return null;
  }

  const employmentStatus = getEmployeeEmploymentStatus(
    employeeId,
    employee.eligibleFrom,
    employee.eligibleUntil,
    employee.leaveOfAbsence?.startDate,
    employee.leaveOfAbsence?.endDate,
    employee.terminationDate
  );
  const { label, icon, color, description } = getEligibilityStatusBadgeConfig(
    employmentStatus,
    employee.eligibleFrom,
    employee.eligibleUntil,
    employee.leaveOfAbsence?.startDate,
    employee.leaveOfAbsence?.endDate,
    employee.createdAt,
    employee.terminationDate,
    hasPreviousEligibility
  );

  return (
    <Group
      bg="zorroGray.10"
      p="xs"
      pb="0.75rem"
      pos="relative"
      style={{ borderRadius: 8 }}
      id="eligibilityMenu"
    >
      <Stack gap="xxs" w="100%" pr="sm">
        <Pill
          variant="with-icon"
          leftSection={icon}
          color={color}
          withOverflowTooltip
        >
          {label}
        </Pill>
        <Text
          c="zorroGray.4"
          size="xs"
          style={{
            paddingLeft:
              'calc(var(--space-2) + var(--space-24) + var(--space-8))',
          }}
        >
          {description}
        </Text>
      </Stack>
      <Box pos="absolute" right="0.6rem" top="0.25rem">
        <Menu
          opened={open}
          onChange={setOpen}
          size="sm"
          position="bottom-start"
        >
          <Menu.Target>
            <ActionIcon
              isChecked={open}
              pos="relative"
              right="2px"
              top="2px"
              size="sm"
            >
              <IconDots size={16} color={brand.zorroCoal900} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              onClick={() => setAction(EmployeeLayoutAction.TERMINATE_EMPLOYEE)}
            >
              Terminate employee
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                setAction(EmployeeLayoutAction.SET_LEAVE_OF_ABSENCE)
              }
            >
              Set a leave of absence
            </Menu.Item>
            {isZorroOperations && (
              <Menu.Item
                onClick={() =>
                  setAction(EmployeeLayoutAction.UPDATE_ELIGIBILITY)
                }
              >
                Update eligibility
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Box>
    </Group>
  );
};
